<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import ReminderTemplateEditPanel from './ReminderTemplateEditPanel';
import TwWysiwyg from '../TwWysiwyg';
import escapable from '../../mixins/escapable';

export default {
  name: 'ReminderTemplateEdit',
  components: {
    ReminderTemplateEditPanel,
    TwWysiwyg,
  },
  mixins: [
    escapable,
  ],
  props: {
    template: {
      type: Object,
      default: undefined,
    },
    show: {
      type: Boolean,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      preview: false,
      templateModel: {
        name: undefined,
        subject: undefined,
        content: undefined,
        attachments: [],
      },
      dialog: false,
      showPanel: false,
      focus: {},
      focusName: '',
      snackbarShow: false,
      newTemplate: false,
    };
  },
  computed: {
    ...mapState({
      loading: state => state.reminders.templates.save.loading,
      error: state => state.reminders.templates.save.error,
      domain: state => state.reminders.templates.domain,
    }),
    ...mapGetters({
      attachmentOptions: 'reminders/getAttachmentOptions',
      replacementOptions: 'reminders/getReplacementOptions',
    }),
    validatedInput() {
      return !!(this.templateModel.name &&
        this.templateModel.subject &&
        this.templateModel.content);
    },
  },
  methods: {
    ...mapActions({
      createTemplate: 'reminders/createTemplate',
      editTemplate: 'reminders/editTemplate',
      getTemplates: 'reminders/getTemplates',
    }),
    replaceValues(value) {
      if (!value) {
        return '';
      }
      let newValue = value;
      this.replacementOptions.forEach((option) => {
        const regex = `:${option.value}:`;
        newValue = newValue.replace(new RegExp(regex, 'g'), option.example);
      });
      return newValue;
    },
    setFocus(event, name) {
      this.$set(this, 'focus', event ? event.target : {});
      this.$set(this, 'focusName', name);
    },
    setFroalaFocus(event, name) {
      this.$set(this, 'focus', event);
      this.$set(this, 'focusName', name);
    },
    insertValue(value) {
      if (this.focus && this.focusName) {
        if (this.focus.event) {
          this.focus.event.target.focus();
          this.focus.editor.html.insert(`:${value.value}:`);
        } else {
          const insert = this.focus.selectionStart;
          this.focus.focus();
          const text = this.templateModel[this.focusName] || '';
          const textBefore = text.substring(0, insert);
          const textAfter = text.substring(insert, text.length);
          this.$set(this.templateModel, this.focusName, `${textBefore}:${value.value}:${textAfter}`);
        }
      }
    },
    previewTemplate() {
      this.preview = !this.preview;
    },
    saveTemplate() {
      if (this.loading) {
        return;
      }

      const template = {
        ...this.templateModel,
        domainId: this.domain.id,
        attachments: this.templateModel.attachments.map(a => a.id),
      };
      const update = this.type === 'edit' ?
        this.editTemplate(template) :
        this.createTemplate(template);
      update.then(() => {
        if (!this.error) {
          this.closeDialog();
          this.getTemplates(this.domain.id);
        }
      });
    },
    closeDialog() {
      this.dialog = false;
      this.showPanel = false;
    },
    escapeHandler() {
      if (this.dialog) {
        this.closeDialog();
      }
    },
  },
  watch: {
    show() {
      this.showPanel = false;
      this.dialog = true;
      this.newTemplate = this.type === 'create';
      if (!this.newTemplate || this.template) {
        this.templateModel = Object.assign({}, this.template);
      } else {
        this.templateModel = {
          subject: undefined,
          name: undefined,
          attachments: [],
          content: undefined,
        };
      }
      setTimeout(() => {
        this.showPanel = true;
      }, 400);
    },
    error(val) {
      if (val) {
        this.snackbarShow = true;
      }
    },
  },
};
</script>

<template>
  <v-container>
    <v-dialog
      v-model="dialog"
      max-width="60%"
      persistent
      no-click-animation
    >
      <v-card v-if="dialog">
        <v-card-title>
          <h1>Reminder Template</h1>
        </v-card-title>
        <v-divider/>
        <v-progress-linear
          v-if="loading"
          :active="loading"
          :indeterminate="true"
          :height="4"
          class="my-0"
        />
        <v-card-text>
          <v-layout row wrap pb-4>
            <v-flex xs12>
              <v-text-field
                v-if="!preview"
                v-model="templateModel.name"
                @focus="setFocus"
                :disabled="loading"
                label="Template Name"
                hint="For internal use only"
                class="text-field-height-fix"
              />
              <div v-else class="mb-2">
                <h3>Name: </h3>
                <span class="pl-2">{{ templateModel.name }}</span>
              </div>
            </v-flex>

            <v-flex xs12>
              <v-text-field
                v-if="!preview"
                v-model="templateModel.subject"
                @focus="setFocus($event, 'subject')"
                :disabled="loading"
                label="Subject"
              />
              <div v-else class="mb-2">
                <h3>Subject: </h3>
                <span class="pl-2">{{ replaceValues(templateModel.subject) }}</span>
              </div>
            </v-flex>

            <v-flex xs12>
              <v-select
                v-if="!preview"
                v-model="templateModel.attachments"
                :items="attachmentOptions"
                label="Attachments"
                item-text="name"
                item-value="id"
                no-data-text="No more attachments available"
                hide-selected
                multiple
                clearable
                chips
                deletable-chips
                return-object
                dense
              />
              <div v-else class="mb-2">
                <h3>Attachments: </h3>
                <span v-if="templateModel.attachments.length" class="pl-2 font-weight-bold font-italic">
                  {{ templateModel.attachments.map(a => a.name).join(', ') }}
                </span>
                <span v-else class="pl-2 grey--text font-italic">
                  No Attachments Chosen
                </span>
              </div>
            </v-flex>

            <v-flex xs12>
              <tw-wysiwyg
                v-if="!preview"
                @focus="setFroalaFocus($event, 'content')"
                @change="templateModel.content = $event"
                :value="templateModel.content"
                :disabled="loading"
                class="mt-2"
              />
              <div v-else>
                <h3>Content: </h3>
                <v-divider class="mt-2"/>
                <div v-html="replaceValues(templateModel.content)" class="pl-2"/>
                <v-divider v-if="templateModel.content" class="mt-2"/>
              </div>
            </v-flex>
          </v-layout>
        </v-card-text>
        <reminder-template-edit-panel
          v-if="showPanel"
          @preview="previewTemplate"
          @value="insertValue"
          @save="saveTemplate"
          @close="closeDialog"
          :preview="preview"
          :validated="validatedInput"
          :has-focus="!!focusName"
        />
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snackbarShow">Unable to edit the template</v-snackbar>
  </v-container>
</template>



// WEBPACK FOOTER //
// src/components/Reminders/ReminderTemplateEdit.vue